/**
 * Users Service
 * Handles user management operations including Auth users and Firestore profiles
 */
import { callFunction } from '../firebase/functions.js'
import { getFirebaseDb } from '../firebase/firebase.js'
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import logger from './logger.js'

const log = logger.createScoped('UsersService')

// Collection name for user profiles
const USERS_COLLECTION = 'Users'

/**
 * List all Firebase Auth users
 * Calls the adminListAuthUsers callable function
 * @returns {Promise<Object>} Result with users array and count
 */
export async function listAuthUsers() {
  log.info('Fetching auth users list')

  try {
    const result = await callFunction('adminListAuthUsers', {})

    log.info('Auth users list fetched', {
      count: result.count,
    })

    return result
  } catch (error) {
    log.error('Failed to fetch auth users', error)
    throw error
  }
}

/**
 * Create a new Firebase Auth user
 * Calls the adminCreateAuthUser callable function
 * @param {Object} payload - User creation payload
 * @param {string} payload.email - Required email
 * @param {string} [payload.password] - Optional password (generated if not provided)
 * @param {string} [payload.displayName] - Optional display name
 * @param {string} [payload.phoneNumber] - Optional phone number
 * @param {string|null} [payload.role] - Optional role ('admin'|'client'|'agent'|null)
 * @returns {Promise<Object>} Result with uid, created, and optionally tempPassword
 */
export async function createAuthUser(payload) {
  log.info('Creating new auth user', { email: payload.email })

  try {
    const result = await callFunction('adminCreateAuthUser', payload)

    log.info('Auth user created', {
      uid: result.uid,
      hasTempPassword: !!result.tempPassword,
    })

    return result
  } catch (error) {
    log.error('Failed to create auth user', error)
    throw error
  }
}

/**
 * Set Auth user disabled state
 * Calls the adminSetAuthDisabled callable function
 * @param {string} uid - User ID
 * @param {boolean} disabled - Disabled state
 * @returns {Promise<Object>} Result with ok, uid, disabled
 */
export async function setAuthDisabled(uid, disabled) {
  log.info('Setting auth disabled state', { uid, disabled })

  try {
    const result = await callFunction('adminSetAuthDisabled', { uid, disabled })

    log.info('Auth disabled state updated', {
      uid: result.uid,
      disabled: result.disabled,
    })

    return result
  } catch (error) {
    log.error('Failed to set auth disabled state', error)
    throw error
  }
}

/**
 * Set user role claim
 * Calls the adminSetUserRoleClaim callable function
 * @param {string} uid - User ID
 * @param {string|null} role - Role ('admin'|'client'|'agent'|null)
 * @returns {Promise<Object>} Result with ok, uid, role
 */
export async function setUserRole(uid, role) {
  log.info('Setting user role claim', { uid, role })

  try {
    const result = await callFunction('adminSetUserRoleClaim', { uid, role })

    log.info('User role claim updated', {
      uid: result.uid,
      role: result.role,
    })

    return result
  } catch (error) {
    log.error('Failed to set user role', error)
    throw error
  }
}

/**
 * Subscribe to realtime updates of user profiles in Firestore
 * @param {Function} callback - Callback receiving a map of { [uid]: profileDoc }
 * @returns {Function} Unsubscribe function
 */
export function subscribeUserProfiles(callback) {
  log.debug('Subscribing to user profiles')

  const db = getFirebaseDb()
  const usersRef = collection(db, USERS_COLLECTION)

  const unsubscribe = onSnapshot(
    usersRef,
    (snapshot) => {
      const profilesByUid = {}

      snapshot.docs.forEach((doc) => {
        profilesByUid[doc.id] = {
          id: doc.id,
          ...doc.data(),
        }
      })

      log.debug('User profiles updated', { count: Object.keys(profilesByUid).length })
      callback(profilesByUid)
    },
    (error) => {
      log.error('User profiles subscription error', error)
      callback({}, error)
    }
  )

  return unsubscribe
}

/**
 * Compute the linked_makats array from a primary client_makat and sub_clients array.
 * This is the authoritative list of client makats the user is allowed to act as,
 * used by firestore.rules#isLinkedToClient to gate client-scoped writes
 * (TOS, orders, etc.) without having to write a session-active makat to Firestore.
 */
function computeLinkedMakats(clientMakat, subClients) {
  const arr = []
  if (clientMakat) arr.push(String(clientMakat))
  if (Array.isArray(subClients)) {
    for (const sc of subClients) {
      if (sc?.makat) arr.push(String(sc.makat))
    }
  }
  return [...new Set(arr)]
}

/**
 * Update a user's Firestore profile.
 * When the patch touches `client_makat` or `sub_clients`, recomputes
 * `linked_makats` from the merged view so Firestore rules stay in sync.
 * @param {string} uid - User ID
 * @param {Object} data - Profile data to update
 * @returns {Promise<void>}
 */
export async function updateUserProfile(uid, data) {
  log.info('Updating user profile', { uid })

  try {
    const db = getFirebaseDb()
    const docRef = doc(db, USERS_COLLECTION, uid)

    const touchesLinks = 'client_makat' in data || 'sub_clients' in data

    const updateData = {
      ...data,
      last_update: serverTimestamp(),
    }

    if (touchesLinks) {
      const snap = await getDoc(docRef)
      const cur = snap.exists() ? snap.data() : {}
      const nextPrimary = 'client_makat' in data ? data.client_makat : cur.client_makat
      const nextSubs = 'sub_clients' in data ? data.sub_clients : cur.sub_clients
      updateData.linked_makats = computeLinkedMakats(nextPrimary, nextSubs)
    }

    await setDoc(docRef, updateData, { merge: true })

    log.info('User profile updated', { uid })
  } catch (error) {
    log.error('Failed to update user profile', error)
    throw error
  }
}

/**
 * Delete a Firebase Auth user (hard delete)
 * Also marks the Firestore profile as deleted if it exists
 * Calls the adminDeleteAuthUser callable function
 * @param {string} uid - User ID to delete
 * @returns {Promise<Object>} Result with ok, uid, authDeleted, profileUpdated
 */
export async function deleteAuthUser(uid) {
  log.info('Deleting auth user', { uid })

  try {
    const result = await callFunction('adminDeleteAuthUser', { uid })

    log.info('Auth user deleted', {
      uid: result.uid,
      authDeleted: result.authDeleted,
      profileUpdated: result.profileUpdated,
    })

    return result
  } catch (error) {
    log.error('Failed to delete auth user', error)
    throw error
  }
}
